import { RefObject, useCallback } from 'react';
import type { Swiper as SwiperInstance } from 'swiper';

export type TapNavAction = 'next' | 'prev' | 'none';

/**
 * Pure, testable logic to determine navigation action based on tap coordinates.
 * This function is exported to be easily unit-tested.
 *
 * @param x - The x-coordinate of the tap relative to the container.
 * @param y - The y-coordinate of the tap relative to the container.
 * @param width - The width of the container.
 * @param height - The height of the container.
 * @returns The navigation action to perform ('next', 'prev', or 'none').
 */
export const determineTapAction = (x: number, y: number, width: number, height: number): TapNavAction => {
  // The feature is only active in the top 7/8 of the gallery
  const isTopActiveArea = y < (height / 8) * 7;
  if (!isTopActiveArea) {
    return 'none';
  }

  const leftQuarterBoundary = width / 4;

  // Tapping in the first 1/4 of the width moves to the previous slide
  if (x < leftQuarterBoundary) {
    return 'prev';
  }

  // Tapping in the remaining 3/4 of the width moves to the next slide
  if (x > leftQuarterBoundary) {
    return 'next';
  }

  // Tapping exactly on the boundary line does nothing
  return 'none';
};

interface UseGalleryTapNavigationProps {
  swiperRef: RefObject<SwiperInstance | null>;
}

/**
 * A hook to handle tap/click navigation on the gallery.
 * Returns a click handler to be attached to the gallery container.
 */
export const useGalleryTapNavigation = ({ swiperRef }: UseGalleryTapNavigationProps) => {
  const handleContainerClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    // Prevent navigation if the click originated on an interactive element like a button or link.
    const target = event.target as HTMLElement;
    if (target.closest('button, a')) {
      return;
    }

    if (!swiperRef.current) {
      return;
    }

    const container = event.currentTarget;
    const rect = container.getBoundingClientRect();

    // --- FIX ---
    // Calculate coordinates relative to the container (currentTarget).
    // This is more reliable than offsetX/Y, which are relative to the event.target.
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const action = determineTapAction(x, y, rect.width, rect.height);

    if (action === 'next') {
      swiperRef.current.slideNext();
    } else if (action === 'prev') {
      swiperRef.current.slidePrev();
    }
  }, [swiperRef]);

  return { handleContainerClick };
};
